import { useState, useEffect, useRef } from "react";
import { remapPlayerAvailability } from "../utils/migrateAvailability";
import { defaultCoachColor } from "../utils/coachColors";
import { applyHouseholds } from "../utils/households";
import { addToWaitlist, removeFromWaitlist, moveInWaitlist } from "../utils/waitlist";
import { apiFetch } from "../utils/auth";

// Writes a waitlist onto a group, dropping the field entirely when the queue
// is empty so untouched groups stay byte-identical in kac_data.json.
function withWaitlist(group, next) {
  if (next && next.length > 0) return { ...group, waitlist: next };
  const { waitlist, ...rest } = group;
  return rest;
}

const STORAGE_KEY = "kac-tennis-state";

async function fetchFromServer() {
  try {
    const res = await apiFetch("");
    if (!res.ok) return null;
    return await res.json();
  } catch {
    // Includes an expired session: apiFetch has already signalled that, and
    // the app is on its way back to the sign-in screen.
    return null;
  }
}

async function saveToServer(state) {
  try {
    await apiFetch("", {
      method: "POST",
      body: JSON.stringify(state),
    });
  } catch {}
}

function loadFromLocalStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return null;
}

export function useKACState() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  // Load from server on mount; fall back to localStorage if server has nothing
  useEffect(() => {
    fetchFromServer().then(serverData => {
      if (serverData && serverData.players) {
        setState(serverData);
      } else {
        const local = loadFromLocalStorage();
        if (local) setState(local);
      }
      setLoading(false);
    });
  }, []);

  // Save to server + localStorage whenever state changes
  useEffect(() => {
    if (!state || loading) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveToServer(state), 1000);
    return () => clearTimeout(saveTimer.current);
  }, [state, loading]);

  function loadData(data) {
    setState({
      players: data.players,
      groups: data.groups,
      coaches: data.coaches || [],
      terms: data.terms || [],
      activeTermName: data.activeTermName || "",
    });
  }

  function addCoach(coach) {
    setState(prev => {
      const maxId = (prev.coaches || []).reduce((max, c) => {
        const num = parseInt(String(c.id).replace("C", ""), 10);
        return isNaN(num) ? max : Math.max(max, num);
      }, 0);
      const newCoach = {
        id: `C${String(maxId + 1).padStart(3, "0")}`,
        name: coach.name,
        maxHours: coach.maxHours ?? null,
        notes: coach.notes || "",
        email: coach.email || "",
        phone: coach.phone || "",
        availability: coach.availability ?? null,
        // Fall back to a palette color keyed off how many coaches already
        // exist, so a fresh coach always lands on a distinct-looking tag.
        color: coach.color || defaultCoachColor((prev.coaches || []).length),
      };
      return { ...prev, coaches: [...(prev.coaches || []), newCoach] };
    });
  }

  function updateCoach(coachId, updates) {
    setState(prev => ({
      ...prev,
      coaches: (prev.coaches || []).map(c =>
        c.id === coachId ? { ...c, ...updates } : c
      ),
    }));
  }

  // Removing a coach also clears them from any group they were assigned to,
  // so no group is left pointing at a coach that no longer exists.
  function deleteCoach(coachId) {
    setState(prev => ({
      ...prev,
      coaches: (prev.coaches || []).filter(c => c.id !== coachId),
      groups: prev.groups.map(g =>
        g.coachId === coachId ? { ...g, coachId: null } : g
      ),
    }));
  }

  function addPlayers(newPlayers) {
    setState(prev => ({
      ...prev,
      players: [...prev.players, ...newPlayers],
    }));
  }

  // Stamps householdId onto players from an uploaded member import file, so
  // either parent's email pulls up the whole family in "My Schedule".
  // Returns the link report for the caller to surface.
  function linkHouseholds(households) {
    if (!state?.players) return { linked: 0, households: 0, unmatched: [] };
    // Computed from the current snapshot (an import is a single deliberate
    // action) so the caller gets the report back synchronously.
    const result = applyHouseholds(state.players, households);
    setState(prev => ({ ...prev, players: result.players }));
    return { linked: result.linked, households: result.households, unmatched: result.unmatched };
  }

  function resetData() {
    localStorage.removeItem(STORAGE_KEY);
    setState(null);
    saveToServer(null);
  }

  function removeFromGroup(playerId, groupId) {
    setState(prev => ({
      ...prev,
      players: prev.players.map(p =>
        p.id === playerId ? { ...p, groupId: null } : p
      ),
      groups: prev.groups.map(g =>
        g.id === groupId
          ? { ...g, playerIds: g.playerIds.filter(id => id !== playerId) }
          : g
      ),
    }));
  }

  function deleteGroup(groupId) {
    setState(prev => ({
      ...prev,
      players: prev.players.map(p =>
        p.groupId === groupId ? { ...p, groupId: null } : p
      ),
      groups: prev.groups.filter(g => g.id !== groupId),
    }));
  }

  function createGroup(groupData) {
    setState(prev => {
      const maxId = prev.groups.reduce((max, g) => {
        const num = parseInt(g.id.replace("G", ""), 10);
        return isNaN(num) ? max : Math.max(max, num);
      }, 0);
      const newId = groupData.id || `G${String(maxId + 1).padStart(3, "0")}`;
      const courtType = groupData.courtType || (groupData.court?.startsWith("Pickleball") ? "Pickleball" : "Tennis");
      // Weekend sessions (open play / privates) set their own duration and can
      // carry a coach + members at creation; the Pickleball=1h rule only
      // applies to instruction groups.
      const isSession = groupData.kind === "private" || groupData.kind === "openplay";
      const newGroup = {
        id: newId,
        tier: groupData.tier,
        courtType,
        day: groupData.day || "",
        startTime: groupData.startTime || "",
        duration: (courtType === "Pickleball" && !isSession) ? 1 : (groupData.duration || 1),
        court: groupData.court || "",
        playerIds: groupData.playerIds || [],
        notes: groupData.notes || "",
        ...(groupData.kind ? { kind: groupData.kind } : {}),
        ...(groupData.coachId ? { coachId: groupData.coachId } : {}),
      };
      return { ...prev, groups: [...prev.groups, newGroup] };
    });
  }

  function updatePlayer(playerId, updates) {
    setState(prev => ({
      ...prev,
      players: prev.players.map(p =>
        p.id === playerId ? { ...p, ...updates } : p
      ),
    }));
  }

  function remapAvailability() {
    setState(prev => ({
      ...prev,
      players: prev.players.map(p => ({ ...p, availability: remapPlayerAvailability(p) })),
    }));
  }

  // --- Waitlists ---------------------------------------------------------
  // Entries live on the group and never touch player.groupId, so waiting for
  // a group is independent of being in one. See utils/waitlist.js.
  function waitlistAdd(groupId, playerId) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? withWaitlist(g, addToWaitlist(g, playerId)) : g
      ),
    }));
  }

  function waitlistRemove(groupId, playerId) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? withWaitlist(g, removeFromWaitlist(g, playerId)) : g
      ),
    }));
  }

  function waitlistMove(groupId, playerId, delta) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? withWaitlist(g, moveInWaitlist(g, playerId, delta)) : g
      ),
    }));
  }

  function updateGroup(groupId, updates) {
    setState(prev => ({
      ...prev,
      groups: prev.groups.map(g =>
        g.id === groupId ? { ...g, ...updates } : g
      ),
    }));
  }

  function movePlayer(playerId, fromGroupId, toGroupId) {
    setState(prev => {
      const players = prev.players.map(p => {
        if (p.id !== playerId) return p;
        const targetGroup = prev.groups.find(g => g.id === toGroupId);
        // Being placed ends both "waiting for next term" and "can't make any
        // clinic" — neither can be true of someone in a group, and leaving a
        // flag set would misfile them in the roster next time they come out
        // of one.
        const { deferred, noFit, ...rest } = p;
        return { ...rest, groupId: toGroupId, tier: targetGroup ? targetGroup.tier : p.tier };
      });
      const groups = prev.groups.map(g => {
        if (g.id === fromGroupId && fromGroupId !== toGroupId)
          return { ...g, playerIds: g.playerIds.filter(id => id !== playerId) };
        if (g.id === toGroupId) {
          // Being placed satisfies the wait, so the entry goes with it —
          // otherwise someone would sit in a group and queue for it at once.
          const joined = g.playerIds.includes(playerId)
            ? g
            : { ...g, playerIds: [...g.playerIds, playerId] };
          return withWaitlist(joined, removeFromWaitlist(joined, playerId));
        }
        return g;
      });
      return { ...prev, players, groups };
    });
  }

  // --- Terms (seasons) ---------------------------------------------------
  // The live schedule always stays at the top level (players/groups/coaches)
  // so the member lookup and deploy backup keep working unchanged. Saved
  // terms are independent deep-copy snapshots kept alongside in `terms`.
  function makeTermId() {
    return `T${Date.now()}${Math.floor(Math.random() * 1000)}`;
  }

  function snapshotOf(prev, name, id) {
    return {
      id: id || makeTermId(),
      name,
      savedAt: new Date().toISOString(),
      players: JSON.parse(JSON.stringify(prev.players || [])),
      groups: JSON.parse(JSON.stringify(prev.groups || [])),
      coaches: JSON.parse(JSON.stringify(prev.coaches || [])),
    };
  }

  // Insert or replace (by name) a snapshot of the current live state.
  function upsertSnapshot(terms, prev, name) {
    const idx = terms.findIndex(t => t.name === name);
    if (idx >= 0) terms[idx] = snapshotOf(prev, name, terms[idx].id);
    else terms.push(snapshotOf(prev, name));
    return terms;
  }

  // Save the current live schedule as a named term (updating it if the name
  // already exists) and mark it active.
  function saveTerm(name) {
    setState(prev => {
      const terms = upsertSnapshot([...(prev.terms || [])], prev, name);
      return { ...prev, terms, activeTermName: name };
    });
  }

  // Roll over to a new term as an exact clone of the current one. The current
  // term is preserved as a snapshot; the live state stays identical but is
  // relabeled to the new term, which you then edit going forward.
  function startNewTerm(newName) {
    setState(prev => {
      let terms = [...(prev.terms || [])];
      if (prev.activeTermName) terms = upsertSnapshot(terms, prev, prev.activeTermName);
      terms = upsertSnapshot(terms, prev, newName);
      return { ...prev, terms, activeTermName: newName };
    });
  }

  // Make a saved term the live schedule. The current term is auto-snapshotted
  // first so no unsaved edits are lost.
  function switchToTerm(termId) {
    setState(prev => {
      let terms = [...(prev.terms || [])];
      const target = terms.find(t => t.id === termId);
      if (!target) return prev;
      if (prev.activeTermName) terms = upsertSnapshot(terms, prev, prev.activeTermName);
      return {
        ...prev,
        players: JSON.parse(JSON.stringify(target.players || [])),
        groups: JSON.parse(JSON.stringify(target.groups || [])),
        coaches: JSON.parse(JSON.stringify(target.coaches || [])),
        activeTermName: target.name,
        terms,
      };
    });
  }

  function deleteTerm(termId) {
    setState(prev => ({ ...prev, terms: (prev.terms || []).filter(t => t.id !== termId) }));
  }

  function renameTerm(termId, name) {
    setState(prev => {
      const target = (prev.terms || []).find(t => t.id === termId);
      const terms = (prev.terms || []).map(t => (t.id === termId ? { ...t, name } : t));
      const activeTermName = target && target.name === prev.activeTermName ? name : prev.activeTermName;
      return { ...prev, terms, activeTermName };
    });
  }

  function duplicateTerm(termId, newName) {
    setState(prev => {
      const terms = [...(prev.terms || [])];
      const src = terms.find(t => t.id === termId);
      if (!src) return prev;
      terms.push({
        id: makeTermId(),
        name: newName,
        savedAt: new Date().toISOString(),
        players: JSON.parse(JSON.stringify(src.players || [])),
        groups: JSON.parse(JSON.stringify(src.groups || [])),
        coaches: JSON.parse(JSON.stringify(src.coaches || [])),
      });
      return { ...prev, terms };
    });
  }

  return {
    players: state?.players ?? null,
    groups: state?.groups ?? null,
    coaches: state?.coaches ?? [],
    terms: state?.terms ?? [],
    activeTermName: state?.activeTermName ?? "",
    loading,
    movePlayer,
    updatePlayer,
    waitlistAdd,
    waitlistRemove,
    waitlistMove,
    updateGroup,
    remapAvailability,
    removeFromGroup,
    createGroup,
    deleteGroup,
    loadData,
    addPlayers,
    linkHouseholds,
    addCoach,
    updateCoach,
    deleteCoach,
    resetData,
    saveTerm,
    startNewTerm,
    switchToTerm,
    deleteTerm,
    renameTerm,
    duplicateTerm,
  };
}
